import { existsSync, accessSync, constants } from 'node:fs';
import { watch } from 'node:fs';
import { LogTail } from '../core/log-tail.js';
import chalk from 'chalk';
import { formatEvent, logger, banner } from '../core/logger.js';
import { assessNginxRequest, attackSeverity, autoDetectParser, configureAttackDetection, parseAuthLog, parseNginxLog } from '../core/log-parser.js';
import { loadConfig } from '../core/config.js';
import { initStateDB, insertEvent } from '../core/state.js';
import type { ThreatEvent, EventSeverity, EventCategory } from '../types/events.js';

interface MonitorOptions {
  module?: string;
  tui?: boolean;
  demo?: boolean;
}

interface LogWatcher {
  path: string;
  name: string;
  category: EventCategory;
}

const LOG_SOURCES: LogWatcher[] = [
  { path: '/var/log/auth.log', name: 'ssh-guard', category: 'auth' },
  { path: '/var/log/secure', name: 'ssh-guard', category: 'auth' },
  { path: '/var/log/nginx/access.log', name: 'log-watcher', category: 'web' },
  { path: '/var/log/syslog', name: 'log-watcher', category: 'system' },
];

export async function monitorCommand(options: MonitorOptions): Promise<void> {
  if (options.tui || options.demo) {
    // Loaded on demand so the plain monitor never pays for the dashboard.
    const { startDashboard } = await import('../tui/dashboard.js');
    await startDashboard({ demo: options.demo });
    return;
  }

  banner();
  logger.info('Starting foreground monitor...');

  const moduleFilter = options.module?.split(',').map((m) => m.trim());

  // Find log files that exist AND are readable by the current user.
  // Unreadable files would crash the tail stream with EACCES, so skip them
  // up-front instead of dying mid-monitor.
  const availableSources: LogWatcher[] = [];
  const unreadable: LogWatcher[] = [];
  for (const s of LOG_SOURCES) {
    if (moduleFilter && !moduleFilter.includes(s.name)) continue;
    if (!existsSync(s.path)) continue;
    try {
      accessSync(s.path, constants.R_OK);
      availableSources.push(s);
    } catch {
      unreadable.push(s);
    }
  }

  if (unreadable.length > 0) {
    logger.warn(`Skipping ${unreadable.length} unreadable log source(s):`);
    for (const s of unreadable) {
      console.log(`  ${chalk.yellow('!')} ${chalk.gray(s.path)} (permission denied — add yourself to the 'adm' group or run as root)`);
    }
    console.log();
  }

  if (availableSources.length === 0) {
    logger.warn('No readable log files found to monitor.');
    logger.info('Available log paths checked:');
    for (const s of LOG_SOURCES) {
      const exists = existsSync(s.path);
      let readable = false;
      if (exists) {
        try { accessSync(s.path, constants.R_OK); readable = true; } catch { readable = false; }
      }
      const glyph = !exists ? chalk.red('✗ missing') : (readable ? chalk.green('✓ readable') : chalk.yellow('! no read perm'));
      console.log(`  ${glyph}  ${s.path}`);
    }
    console.log();
    logger.info('Starting demo mode with synthetic events...\n');
    await runDemoMode();
    return;
  }

  try {
    initStateDB();
  } catch {
    // State DB optional for monitoring
  }

  // The same [detection] settings the daemon uses, so both agree on what is an attack.
  configureAttackDetection(loadConfig().detection);

  logger.info(`Monitoring ${availableSources.length} log source(s):`);
  for (const src of availableSources) {
    console.log(`  ${chalk.green('●')} ${chalk.white(src.name.padEnd(14))} → ${chalk.gray(src.path)}`);
  }
  console.log();
  console.log(chalk.gray('  Press Ctrl+C to stop\n'));

  // Tail each log file
  for (const src of availableSources) {
    tailLog(src);
  }

  // Keep process alive
  await new Promise(() => {});
}

function tailLog(source: LogWatcher): void {
  const { path, name, category } = source;

  // From the end of the file; each new line exactly once (see LogTail).
  const tail = new LogTail(path);
  let warned = false;
  const checkForNewData = () => {
    try {
      tail.poll((line) => processLine(line, name, category));
    } catch (err) {
      if (!warned) logger.warn(`cannot read ${path}: ${(err as NodeJS.ErrnoException).code || (err as Error).message}`);
      warned = true;
    }
  };

  // Poll for changes (more reliable than fs.watch for log files)
  setInterval(checkForNewData, 1000);
}

function processLine(line: string, moduleName: string, category: EventCategory): void {
  const parsed = autoDetectParser(line);
  if (!parsed) return;

  let severity: EventSeverity = 'info';
  let message = line;
  let details: Record<string, unknown> | undefined;

  if (parsed.source === 'auth') {
    const authEntry = parseAuthLog(line);
    if (!authEntry) return;

    if (/failed password/i.test(authEntry.fields.message)) {
      severity = 'high';
      message = `Failed SSH login for ${authEntry.fields.user || 'unknown'} from ${authEntry.fields.ip || 'unknown'}`;
    } else if (/accepted/i.test(authEntry.fields.message)) {
      severity = 'info';
      message = `SSH login accepted for ${authEntry.fields.user || 'unknown'}`;
    } else if (/invalid user/i.test(authEntry.fields.message)) {
      severity = 'high';
      message = `Invalid SSH user attempt: ${authEntry.fields.user || 'unknown'} from ${authEntry.fields.ip || 'unknown'}`;
    }
  } else if (parsed.source === 'nginx') {
    const nginxEntry = parseNginxLog(line);
    if (!nginxEntry) return;

    const status = parseInt(nginxEntry.fields.status);
    const crs = assessNginxRequest(nginxEntry);
    const attack = attackSeverity(crs);
    const type = (crs.attackType ?? 'unknown').toUpperCase();
    if (crs.score > 0) {
      details = {
        attack_type: crs.attackType,
        crs_score: crs.score,
        crs_threshold: crs.threshold,
        crs_rule_ids: crs.matches.map((m) => m.id),
      };
    }

    if (attack) {
      severity = attack;
      message = `Attack detected [${type}]: ${nginxEntry.fields.method} ${nginxEntry.fields.path}`;
    } else if (status >= 500) {
      severity = 'medium';
      message = `Server error ${status}: ${nginxEntry.fields.method} ${nginxEntry.fields.path}`;
    } else if (status >= 400) {
      severity = 'low';
      message = `Client error ${status}: ${nginxEntry.fields.method} ${nginxEntry.fields.path}`;
    } else if (crs.score > 0) {
      severity = 'low';
      message = `Suspicious [${type}] (score ${crs.score}/${crs.threshold}): ${nginxEntry.fields.method} ${nginxEntry.fields.path}`;
    } else {
      severity = 'info';
      message = `${nginxEntry.fields.method} ${nginxEntry.fields.path} → ${status}`;
    }
  }

  const event: ThreatEvent = {
    timestamp: new Date(),
    module: moduleName,
    category,
    severity,
    message,
    source_ip: parsed.fields.ip as string | undefined,
    ...(details ? { details } : {}),
  };

  console.log(formatEvent(event.module, event.severity, event.message, event.source_ip));

  try {
    insertEvent(event);
  } catch {
    // State DB may not be available
  }
}

async function runDemoMode(): Promise<void> {
  const demoEvents: Array<Omit<ThreatEvent, 'timestamp'>> = [
    { module: 'ssh-guard', category: 'auth', severity: 'info', message: 'SSH login accepted for ubuntu', source_ip: '10.0.0.1' },
    { module: 'log-watcher', category: 'web', severity: 'info', message: 'GET /api/health → 200', source_ip: '172.16.0.50' },
    { module: 'ssh-guard', category: 'auth', severity: 'high', message: 'Failed SSH login for root', source_ip: '45.33.22.11' },
    { module: 'log-watcher', category: 'web', severity: 'low', message: 'GET /admin → 404', source_ip: '91.121.44.55' },
    { module: 'ssh-guard', category: 'auth', severity: 'high', message: 'Failed SSH login for admin', source_ip: '45.33.22.11' },
    { module: 'log-watcher', category: 'web', severity: 'critical', message: 'Attack detected [SQLI]: GET /search?q=1%27+UNION+SELECT+username,password+FROM+users--', source_ip: '185.220.101.44' },
    { module: 'log-watcher', category: 'web', severity: 'medium', message: 'Server error 502: GET /api/users', source_ip: '10.0.0.2' },
    { module: 'ssh-guard', category: 'auth', severity: 'high', message: 'Invalid SSH user attempt: admin123', source_ip: '103.77.88.99' },
    { module: 'log-watcher', category: 'web', severity: 'critical', message: 'Attack detected [PATH_TRAVERSAL]: GET /../../etc/passwd', source_ip: '185.220.101.44' },
    { module: 'ssh-guard', category: 'auth', severity: 'high', message: 'Brute force detected — 6 failures in 45s', source_ip: '45.33.22.11' },
    { module: 'log-watcher', category: 'system', severity: 'medium', message: 'Unusual outbound connection to 198.51.100.1:4444', source_ip: '198.51.100.1' },
    { module: 'log-watcher', category: 'web', severity: 'info', message: 'GET /index.html → 200', source_ip: '172.16.0.51' },
  ];

  let i = 0;
  const interval = setInterval(() => {
    const event = demoEvents[i % demoEvents.length];
    console.log(formatEvent(event.module, event.severity, event.message, event.source_ip));
    i++;
  }, 1500 + Math.random() * 2000);

  process.on('SIGINT', () => {
    clearInterval(interval);
    console.log(chalk.gray('\n  Monitor stopped.'));
    process.exit(0);
  });

  await new Promise(() => {});
}
